import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import { fetchMatchParticipantInsights } from 'tio-data/requests/match-participant';
import type { AuthenticatedSyfRouteModel } from '../syf';
import type { MatchPlanBenefitYear } from 'tio-common/types/secure-your-future';
import type { AccountTransactionExtension as AccountTransactionModel } from 'tio-data/data/resources/account-transaction.ext';
import type { MatchPayment as MatchPaymentModel } from 'tio-data/data/resources/match-payment.type';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

export default class AuthenticatedSyfDashboardRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  queryParams = {
    benefitYear: { refreshModel: true, replace: true },
  };

  beforeModel() {
    if (!this.sessionContext.currentEmployee?.matchParticipant?.isEnrolled) {
      this.router.transitionTo('authenticated.syf.enrollment');
    }
  }

  async model(params: { benefitYear: string }) {
    const { content: personContent } = (await this.store.request(
      findRecord<PersonModel>(
        'person',
        // @ts-expect-error type models
        this.sessionContext.user.person.id,
        {
          include: 'accounts.financial-institution',
          reload: true,
        }
      )
    )) as { content: { data: PersonModel } };
    const person = personContent.data;

    const parentModel = this.modelFor('authenticated.syf') as AuthenticatedSyfRouteModel;
    if (!parentModel) {
      this.router.transitionTo('authenticated.syf.enrollment');
      // Transition aborts this model hook, but TypeScript needs a return
      throw new Error('Redirecting to enrollment');
    }

    const { matchParticipant } = parentModel;
    await fetchMatchParticipantInsights(this.store, matchParticipant);

    let { benefitYear } = params;
    if (!benefitYear) {
      const currentDate = new Date();
      const metadata = matchParticipant.matchPlan.metadata;

      // Find the most recent benefit year that has not yet ended
      const applicableBenefitYear = Object.values(metadata.benefit_years).find(
        (yearData: MatchPlanBenefitYear) => {
          const cutoffDate = new Date(yearData.final_certification_date);
          return currentDate <= cutoffDate;
        }
      );

      benefitYear = applicableBenefitYear?.benefit_year.toString() ?? '2024';

      this.router.transitionTo({ queryParams: { benefitYear } });
    }

    const [matchPaymentsResult, accountTransactionsResult] = (await Promise.all([
      this.store.request(
        query<MatchPaymentModel>('match-payment', {
          'filter[match-participant]': matchParticipant.id,
          'filter[transaction-year]': benefitYear,
          'filter[include-discarded]': false,
          include: 'account-transaction.financial-institution',
          sort: '-transaction-date',
          'page[limit]': 200,
        })
      ),
      this.store.request(
        query<AccountTransactionModel>('account-transaction', {
          'filter[person]': person.id,
          'filter[transaction-year]': benefitYear,
          'filter[include-discarded]': false,
          sort: '-transaction-date',
          'page[limit]': 200,
        })
      ),
    ])) as [
      { content: { data: MatchPaymentModel[] } },
      { content: { data: AccountTransactionModel[] } },
    ];
    const { content: matchPaymentsContent } = matchPaymentsResult;
    const { content: accountTransactionsContent } = accountTransactionsResult;

    return {
      accounts: person.activeAccounts,
      matchParticipant: matchParticipant,
      matchPlan: matchParticipant.matchPlan,
      matchPayments: matchPaymentsContent.data.slice(),
      accountTransactions: accountTransactionsContent.data.slice(),
    };
  }
}
